'use client'

import React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'

const configuratorSchema = z.object({
  is_configurator_enabled: z.boolean(),
})

const claimsSchema = z.object({
  is_claims_enabled: z.boolean(),
})

const palettesSchema = z.object({
  selectedPalettes: z.array(z.number()),
  action: z.enum(['add', 'replace', 'remove']),
})

const bulkEditSchema = z.union([
  configuratorSchema,
  claimsSchema,
  palettesSchema,
])

interface BulkEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => void
  selectedCustomers: any[]
  isLoading?: boolean
  selectionMode?: 'individual' | 'all-filtered'
  totalFilteredCount?: number
  editProperty?: 'configurator' | 'claims' | 'palettes'
  availablePalettes?: any[]
}

export function BulkEditModal({
  isOpen,
  onClose,
  onSubmit,
  selectedCustomers,
  isLoading = false,
  selectionMode = 'individual',
  totalFilteredCount = 0,
  editProperty = 'configurator',
  availablePalettes = [],
}: BulkEditModalProps) {
  const configuratorForm = useForm({
    resolver: zodResolver(configuratorSchema),
    defaultValues: {
      is_configurator_enabled: false,
    },
  })

  const claimsForm = useForm({
    resolver: zodResolver(claimsSchema),
    defaultValues: {
      is_claims_enabled: false,
    },
  })

  const palettesForm = useForm({
    resolver: zodResolver(palettesSchema),
    defaultValues: {
      selectedPalettes: [] as number[],
      action: 'add' as const,
    },
  })

  const handleSubmit = (data: any) => {
    onSubmit(data)
  }

  const handleClose = () => {
    configuratorForm.reset()
    claimsForm.reset()
    palettesForm.reset()
    onClose()
  }

  const getPropertyTitle = () => {
    switch (editProperty) {
      case 'configurator':
        return 'Configurator Access'
      case 'claims':
        return 'Claims Portal'
      case 'palettes':
        return 'Fabric Palettes'
      default:
        return 'Bulk Edit'
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{getPropertyTitle()} - Bulk Edit</DialogTitle>
          <DialogDescription>
            {selectionMode === 'all-filtered' ? (
              <>
                Bulk edit {totalFilteredCount} filtered customer
                {totalFilteredCount === 1 ? '' : 's'}
              </>
            ) : (
              <>
                Bulk edit {selectedCustomers.length} selected customer
                {selectedCustomers.length === 1 ? '' : 's'}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {editProperty === 'configurator' ? (
          <Form {...configuratorForm}>
            <form
              onSubmit={configuratorForm.handleSubmit(handleSubmit)}
              className="space-y-6"
            >
              <div className="space-y-4">
                <FormField
                  control={configuratorForm.control}
                  name="is_configurator_enabled"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Enable Configurator Access
                        </FormLabel>
                        <FormDescription>
                          Allow customers to access the product configurator
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Updating...
                    </div>
                  ) : selectionMode === 'all-filtered' ? (
                    `Update ${totalFilteredCount} Customer${
                      totalFilteredCount === 1 ? '' : 's'
                    }`
                  ) : (
                    `Update ${selectedCustomers.length} Customer${
                      selectedCustomers.length === 1 ? '' : 's'
                    }`
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        ) : editProperty === 'claims' ? (
          <Form {...claimsForm}>
            <form
              onSubmit={claimsForm.handleSubmit(handleSubmit)}
              className="space-y-6"
            >
              <div className="space-y-4">
                <FormField
                  control={claimsForm.control}
                  name="is_claims_enabled"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Enable Claims Portal
                        </FormLabel>
                        <FormDescription>
                          Allow customers to access the claims portal
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Updating...
                    </div>
                  ) : selectionMode === 'all-filtered' ? (
                    `Update ${totalFilteredCount} Customer${
                      totalFilteredCount === 1 ? '' : 's'
                    }`
                  ) : (
                    `Update ${selectedCustomers.length} Customer${
                      selectedCustomers.length === 1 ? '' : 's'
                    }`
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        ) : (
          <Form {...palettesForm}>
            <form
              onSubmit={palettesForm.handleSubmit(handleSubmit)}
              className="space-y-6"
            >
              <div className="space-y-4">
                <FormField
                  control={palettesForm.control}
                  name="action"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Action</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select action" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="add">
                            Add palettes to customers
                          </SelectItem>
                          <SelectItem value="replace">
                            Replace customer palettes
                          </SelectItem>
                          <SelectItem value="remove">
                            Remove palettes from customers
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Choose how to modify the palettes for selected customers
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={palettesForm.control}
                  name="selectedPalettes"
                  render={() => (
                    <FormItem>
                      <div className="mb-4">
                        <FormLabel className="text-base">
                          Fabric Palettes
                        </FormLabel>
                        <FormDescription>
                          Select the palettes to apply to customers
                        </FormDescription>
                      </div>
                      <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                        {availablePalettes.map(palette => (
                          <FormField
                            key={palette.id}
                            control={palettesForm.control}
                            name="selectedPalettes"
                            render={({ field }) => {
                              return (
                                <FormItem
                                  key={palette.id}
                                  className="flex flex-row items-start space-x-3 space-y-0"
                                >
                                  <FormControl>
                                    <Checkbox
                                      checked={field.value?.includes(
                                        palette.id
                                      )}
                                      onCheckedChange={checked => {
                                        const currentValue = field.value || []
                                        return checked
                                          ? field.onChange([
                                              ...currentValue,
                                              palette.id,
                                            ])
                                          : field.onChange(
                                              currentValue.filter(
                                                value => value !== palette.id
                                              )
                                            )
                                      }}
                                    />
                                  </FormControl>
                                  <FormLabel className="font-normal">
                                    {palette.name}
                                  </FormLabel>
                                </FormItem>
                              )
                            }}
                          />
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Updating...
                    </div>
                  ) : selectionMode === 'all-filtered' ? (
                    `Update ${totalFilteredCount} Customer${
                      totalFilteredCount === 1 ? '' : 's'
                    }`
                  ) : (
                    `Update ${selectedCustomers.length} Customer${
                      selectedCustomers.length === 1 ? '' : 's'
                    }`
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
